import { CommandGroup, CommandItem, CommandList, Icon, Skeleton } from "@/shared/components";
import { cn } from "@/shared/utils";
import { Popover, PopoverContent, PopoverPortal, PopoverTrigger } from "@radix-ui/react-popover";
import { Command as CommandPrimitive } from "cmdk";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type CommandFilter = (value: string, search: string, keywords?: string[]) => number;

export type Option<D extends Record<string, unknown> = Record<string, unknown>> = D & {
    value: string;
    label: string;
};

export type RenderOptionType<D extends Record<string, unknown>> = (option: Option<D>) => React.ReactNode;

interface AutoCompleteProps<D extends Record<string, unknown>> {
    options: Option<D>[];
    renderOption?: RenderOptionType<D>;
    emptyMessage: string;
    value?: string;
    onChange?: (value: string) => void;
    isLoading?: boolean;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    hasError: boolean;
    showSearchIcon?: boolean;
    allowCustomValue?: boolean;
    rankFn?: CommandFilter;
    disableBuiltInFilter?: boolean;
    onBlur?: () => void;
    ref?: (instance: HTMLInputElement | null) => void;
    "data-testid"?: string;
}

export function AutoComplete<D extends Record<string, unknown>>({
    options,
    renderOption,
    placeholder,
    emptyMessage,
    value,
    onChange,
    onBlur,
    disabled,
    className,
    hasError,
    showSearchIcon = true,
    isLoading = false,
    allowCustomValue = false,
    rankFn,
    disableBuiltInFilter = false,
    ref = () => {},
    "data-testid": dataTestid,
}: Readonly<AutoCompleteProps<D>>) {
    const [isOpen, setIsOpen] = useState(false);
    const selectedOption = options.find((opt) => opt.value.toLowerCase() === value?.toLowerCase());
    const [selected, setSelected] = useState<Option<D> | undefined>(selectedOption);
    const [inputValue, setInputValue] = useState<string>(selectedOption?.label ?? "");

    useEffect(() => {
        if (selectedOption?.label) {
            setInputValue(selectedOption.label);
        }
    }, [selectedOption]);

    useEffect(() => {
        if (inputValue.trim().length > 0) {
            commandListRef.current?.scrollTo(0, 0);
        }
    }, [inputValue]);

    const inputRef = useRef<HTMLInputElement>(null);
    const commandListRef = useRef<HTMLDivElement>(null);

    const handleValueChange = (val: string) => {
        // is triggered when typing in the input
        setInputValue(val);
        setIsOpen(val !== "");
    };

    const handleBlur = useCallback(() => {
        setInputValue(selected?.label ?? "");
        onBlur?.();
    }, [selected, onBlur]);

    const handleSelectOption = (option: Option<D>) => {
        // is triggered on option click or on enter
        setInputValue(option?.label);
        setSelected(option);
        onChange?.(option.value);
        setIsOpen(false);
    };

    const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setInputValue("");
        setSelected(undefined);
        onChange?.("");
        inputRef.current?.focus();
    };

    const filteredOptions = useMemo(() => {
        if (!rankFn) return options;

        const scored = options
            .map((opt) => ({
                opt,
                score: rankFn(opt.label, inputValue, [opt.value]),
            }))
            .filter(({ score }) => score > 0)
            .sort((a, b) => b.score - a.score);

        return scored.map(({ opt }) => opt);
    }, [options, inputValue, rankFn]);

    const showCustomValueOption =
        allowCustomValue &&
        inputValue.trim() !== "" &&
        !options.some((o) => o.value.toLowerCase() === inputValue.toLowerCase() || o.label.toLowerCase() === inputValue.toLowerCase());

    const shouldShowEmptyMessage = !isLoading && filteredOptions.length === 0 && !showCustomValueOption;

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen} modal>
            <CommandPrimitive filter={rankFn} shouldFilter={!disableBuiltInFilter}>
                <PopoverTrigger asChild>
                    <div
                        className={cn(
                            "border-input-default bg-input placeholder:text-content-placeholder hover:border-interactive-hover focus-within:outline-focus rounded-m text-s relative flex h-10 w-full items-center border py-2 pr-3 focus-within:border-transparent focus-within:outline-2 disabled:cursor-not-allowed disabled:opacity-50",
                            "aria-invalid:outline-error aria-invalid:border-transparent aria-invalid:outline-2",
                            showSearchIcon && "pl-3",
                            className
                        )}
                        aria-invalid={hasError ? "true" : undefined}
                        cmdk-input-wrapper=""
                    >
                        {showSearchIcon && <Icon name="search" className="text-content-subtle" />}

                        <CommandPrimitive.Input
                            ref={(e) => {
                                ref(e);
                                inputRef.current = e; // RHF needs a ref but the ref from RHF is a ref callback. Because we also need a regular ref internally, we set that ref here. Docs: https://www.react-hook-form.com/faqs
                            }}
                            value={inputValue}
                            onValueChange={isLoading ? undefined : handleValueChange}
                            onBlur={handleBlur}
                            placeholder={placeholder}
                            onClick={() => setIsOpen(true)}
                            disabled={disabled}
                            className={cn(
                                "rounded-m text-m flex w-full bg-transparent px-2.75 py-1.75 outline-hidden focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
                                className
                            )}
                            data-testid={`${dataTestid}-autocomplete-input`}
                        />
                        {selectedOption && !disabled && (
                            <button
                                type="button"
                                aria-label="Clear"
                                onClick={handleClear}
                                className="text-muted-foreground hover:text-foreground focus:outline-hidden"
                                tabIndex={-1}
                            >
                                <Icon name="close" size="m" className="text-content-emphasis shrink-0 opacity-50" />
                            </button>
                        )}
                        {!selectedOption && (
                            <Icon
                                name="chevron-down"
                                size="m"
                                className={cn("text-content-emphasis transition", isOpen && "rotate-180 transform")}
                            />
                        )}
                    </div>
                </PopoverTrigger>
                <PopoverPortal>
                    <PopoverContent
                        onOpenAutoFocus={(e) => {
                            // keep focus in the input after clicking it.
                            e.preventDefault();
                        }}
                        className="animate-in fade-in-0 zoom-in-95 rounded-bl-l bg-background z-100 outline-hidden"
                        style={{
                            width: "var(--radix-popover-trigger-width)",
                        }}
                    >
                        <CommandList className="rounded-bl-l" ref={commandListRef} data-testid={`${dataTestid}-autocomplete-content`}>
                            {isLoading && (
                                <CommandPrimitive.Loading>
                                    <div className="p-1">
                                        <Skeleton className="mx-4 my-1 h-8 w-9/12" />
                                    </div>
                                </CommandPrimitive.Loading>
                            )}

                            {!isLoading && filteredOptions.length > 0 && (
                                <CommandGroup data-testid="autocomplete-group">
                                    {filteredOptions.map((option) => (
                                        <CommandItem
                                            key={option.value}
                                            value={option.label}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                            onSelect={() => handleSelectOption(option)}
                                            data-testid="autocomplete-item"
                                        >
                                            {renderOption ? renderOption(option) : option.label}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            )}

                            {showCustomValueOption && (
                                <CommandItem
                                    key={inputValue}
                                    value={inputValue}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                    onSelect={() => handleSelectOption({ value: inputValue, label: inputValue } as Option<D>)}
                                    data-testid={`${dataTestid}-autocomplete-create-item`}
                                >
                                    Gebruik "{inputValue}"
                                </CommandItem>
                            )}

                            {shouldShowEmptyMessage && (
                                <CommandPrimitive.Empty className="text-s rounded-sm px-2 py-3 text-center select-none">
                                    {emptyMessage}
                                </CommandPrimitive.Empty>
                            )}
                        </CommandList>
                    </PopoverContent>
                </PopoverPortal>
            </CommandPrimitive>
        </Popover>
    );
}
